import React, { useState } from "react";
import { Client } from "../../interfaces/client";
import { AdditionalContact } from "../../interfaces/additionalContact";
import { createAdditionalContact } from "../../services/additionalContactService";
import { checkEmailExists } from "../../services/clientService";
import ConfirmPopup from "./ConfirmPopup";
import { isValidEmail } from "../../services/helpers";

interface AddAdditionalProps {
  client: Client;
  onClose: (message?: string) => void;
}

const AddAdditionalContact: React.FC<AddAdditionalProps> = ({
  client,
  onClose,
}) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [additionalContact, setAdditionalContact] =
    useState<AdditionalContact | null>(null);
  const [showConfirmationEmail, setShowConfirmationEmail] = useState(false);
  const [confirmHandlers, setConfirmHandlers] = useState<{
    onConfirm: () => void;
    onCancel: () => void;
  } | null>(null);
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string;
  }>({});

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    field: string,
  ) => {
    let value: string | boolean =
      e.target.type === "checkbox"
        ? (e.target as HTMLInputElement).checked
        : e.target.value;

    setValidationErrors((prevErrors) => ({ ...prevErrors, [field]: "" }));

    setAdditionalContact((prevContact) => ({
      ...prevContact!,
      [field]: value,
    }));
  };

  const addAdditionalContact = async () => {
    try {
      if (additionalContact && (await validateFields())) {
        const duplicate = await checkEmailExists(
          additionalContact.contactEmail,
          client.clientID,
        );

        if (duplicate) {
          const confirmed = await confirmEmailOverwrite();

          if (!confirmed) {
            return;
          }
        }

        additionalContact.clientID = client.clientID;
        await createAdditionalContact(additionalContact);
        onClose("Contact added.");
        setErrorMessage("");
      }
    } catch (error) {
      console.error("Error creating additional contact:", error);
      setErrorMessage("");
    }
  };

  const confirmEmailOverwrite = (): Promise<boolean> => {
    return new Promise((resolve) => {
      const handleConfirm = () => {
        setShowConfirmationEmail(false);
        resolve(true);
      };

      const handleCancel = () => {
        setShowConfirmationEmail(false);
        resolve(false);
      };

      setConfirmHandlers({ onConfirm: handleConfirm, onCancel: handleCancel });
      setShowConfirmationEmail(true);
    });
  };

  const validateFields = async () => {
    const errors: { [key: string]: string } = {};

    if (!additionalContact?.contactTitle) {
      errors.contactTitle = "Title is required.";
    }
    if (!additionalContact?.contactFirstName) {
      errors.contactFirstName = "First name is required.";
    }
    if (!additionalContact?.contactLastName) {
      errors.contactLastName = "Last name is required.";
    }
    if (!additionalContact?.contactJobTitle) {
      errors.contactJobTitle = "Job Title is required.";
    }
    if (!additionalContact?.contactEmail) {
      errors.contactEmail = "Email is required.";
    } else if (!isValidEmail(additionalContact.contactEmail)) {
      errors.contactEmail = "Please enter a valid email.";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  return (
    <>
      <div className="background-mask" onClick={() => onClose()}></div>
      <div className="filter-popup-large">
        <div className="close-button-container">
          <button className="close-button" onClick={() => onClose()}>
            <i className="bi bi-x-circle"></i>
          </button>
        </div>
        <div className="filter-header">
          <h2>Add Additional Contact</h2>
        </div>
        <br></br>
        <div
          className="dialog-container"
          style={{ flexDirection: "row", flexWrap: "wrap" }}
        >
          <div className="field-stacked" style={{ flex: "0 0 10%" }}>
            <label className="field-label">Title:</label>
            <div className="field-input">
              <input
                className={`form-control ${
                  validationErrors.contactTitle ? "is-invalid" : ""
                }`}
                type="text"
                placeholder="Mr./Ms., The Rev., ..."
                value={additionalContact?.contactTitle || ""}
                onChange={(e) => handleInputChange(e, "contactTitle")}
              />
              {validationErrors.contactTitle && (
                <div className="invalid-feedback">
                  {validationErrors.contactTitle}
                </div>
              )}
            </div>
          </div>
          <div className="field-stacked" style={{ flex: "0 0 40%" }}>
            <label className="field-label">First Name:</label>
            <div className="field-input">
              <input
                className={`form-control ${
                  validationErrors.contactFirstName ? "is-invalid" : ""
                }`}
                type="text"
                value={additionalContact?.contactFirstName || ""}
                onChange={(e) => handleInputChange(e, "contactFirstName")}
              />
              {validationErrors.contactFirstName && (
                <div className="invalid-feedback">
                  {validationErrors.contactFirstName}
                </div>
              )}
            </div>
          </div>
          <div className="field-stacked" style={{ flex: "0 0 45%" }}>
            <label className="field-label">Last Name:</label>
            <div className="field-input">
              <input
                className={`form-control ${
                  validationErrors.contactLastName ? "is-invalid" : ""
                }`}
                type="text"
                value={additionalContact?.contactLastName || ""}
                onChange={(e) => handleInputChange(e, "contactLastName")}
              />
              {validationErrors.contactLastName && (
                <div className="invalid-feedback">
                  {validationErrors.contactLastName}
                </div>
              )}
            </div>
          </div>
          <div className="field-stacked" style={{ flex: "0 0 20%" }}>
            <label className="field-label">Job Title:</label>
            <div className="field-input">
              <input
                className={`form-control ${
                  validationErrors.contactJobTitle ? "is-invalid" : ""
                }`}
                type="text"
                value={additionalContact?.contactJobTitle || ""}
                onChange={(e) => handleInputChange(e, "contactJobTitle")}
              />
              {validationErrors.contactJobTitle && (
                <div className="invalid-feedback">
                  {validationErrors.contactJobTitle}
                </div>
              )}
            </div>
          </div>
          <div className="field-stacked" style={{ flex: "0 0 50%" }}>
            <label className="field-label">Email:</label>
            <div className="field-input">
              <input
                className={`form-control ${
                  validationErrors.contactEmail ? "is-invalid" : ""
                }`}
                type="text"
                value={additionalContact?.contactEmail || ""}
                onChange={(e) => handleInputChange(e, "contactEmail")}
              />
              {validationErrors.contactEmail && (
                <div className="invalid-feedback">
                  {validationErrors.contactEmail}
                </div>
              )}
            </div>
          </div>
          <div className="field-stacked" style={{ flex: "0 0 10%" }}>
            <label className="field-label">Invoice Manager:</label>
            <div>
              <input
                className={`form-check-input`}
                type="checkbox"
                checked={additionalContact?.invoiceManager || false}
                onChange={(e) => handleInputChange(e, "invoiceManager")}
              />
            </div>
          </div>
        </div>
        {errorMessage && (
          <div className="error-message red" style={{ color: "red" }}>
            {errorMessage}
          </div>
        )}
        <div className="filter-actions">
          <button
            className={`oxbtn btn-green ${
              !additionalContact?.contactEmail ? "disabled" : ""
            }`}
            onClick={addAdditionalContact}
            disabled={!additionalContact?.contactEmail}
          >
            Save
          </button>
        </div>
      </div>
      {showConfirmationEmail && confirmHandlers && (
        <ConfirmPopup
          onConfirm={confirmHandlers.onConfirm}
          onCancel={confirmHandlers.onCancel}
          message="There is another client containing this email. Are you sure you want to link them?"
          btnColor="green"
        />
      )}
    </>
  );
};

export default AddAdditionalContact;
